import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  ChevronLeft, 
  ChevronRight,
  Plus,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Clock,
  Edit,
  Trash2
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CalendarPage = ({ user, logout, token }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const platformIcons = {
    twitter: <Twitter className="w-3 h-3" />,
    instagram: <Instagram className="w-3 h-3" />,
    facebook: <Facebook className="w-3 h-3" />,
    linkedin: <Linkedin className="w-3 h-3" />,
    youtube: <Youtube className="w-3 h-3" />,
    tiktok: <span className="text-[10px] font-bold">TT</span>,
    pinterest: <span className="text-[10px] font-bold">P</span>
  };

  const platformColors = {
    twitter: "bg-blue-500",
    instagram: "bg-pink-500",
    facebook: "bg-indigo-500",
    linkedin: "bg-sky-500",
    youtube: "bg-red-500",
    tiktok: "bg-gray-800",
    pinterest: "bg-red-700"
  };

  const statusColors = {
    draft: "bg-muted text-muted-foreground border-muted",
    scheduled: "bg-accent/20 text-accent border-accent/30",
    published: "bg-primary/20 text-primary border-primary/30"
  };

  useEffect(() => {
    fetchPosts();
  }, [currentDate, token]);

  const fetchPosts = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/posts/calendar/${year}/${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPostsForDate = (date) => {
    return posts.filter(post => {
      if (!post.scheduled_at) return false;
      const postDate = new Date(post.scheduled_at);
      return isSameDay(postDate, date);
    });
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post eliminado");
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      toast.error("Error al eliminar el post");
    }
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate)
  });

  const firstDayOfWeek = startOfMonth(currentDate).getDay();
  const emptyDays = Array(firstDayOfWeek).fill(null);

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Calendario</h1>
              <p className="text-muted-foreground mt-1">
                Gestiona tus publicaciones programadas
              </p>
            </div>
            <Link to="/composer">
              <Button className="gradient-neural glow-neural text-white" data-testid="new-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                Programar Post
              </Button>
            </Link>
          </div>

          <Card data-testid="calendar-card">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-xl font-semibold">
                {format(currentDate, "MMMM yyyy", { locale: es })}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setCurrentDate(new Date())}
                  data-testid="today-btn"
                >
                  Hoy
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {/* Day Headers */}
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
                  <div 
                    key={day}
                    className="bg-secondary/50 p-3 text-center text-sm font-medium text-muted-foreground"
                  >
                    {day}
                  </div>
                ))}
                
                {/* Empty Days */}
                {emptyDays.map((_, index) => (
                  <div key={`empty-${index}`} className="bg-card min-h-[100px] p-2"></div>
                ))}
                
                {/* Calendar Days */}
                {daysInMonth.map((date) => {
                  const dayPosts = getPostsForDate(date);
                  const isToday = isSameDay(date, new Date());
                  
                  return (
                    <div 
                      key={date.toISOString()}
                      className={`bg-card min-h-[100px] p-2 cursor-pointer hover:bg-secondary/30 transition-colors ${
                        isToday ? 'ring-2 ring-primary ring-inset' : ''
                      }`}
                      onClick={() => setSelectedDate(date)}
                      data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : ''}`}>
                        {format(date, 'd')}
                      </div>
                      <div className="space-y-1">
                        {dayPosts.slice(0, 3).map((post) => (
                          <div 
                            key={post.post_id}
                            className={`text-xs p-1 rounded truncate cursor-pointer ${statusColors[post.status]}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPost(post);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              {post.platforms?.[0] && platformIcons[post.platforms[0]]}
                              <span className="truncate">{post.content.substring(0, 20)}...</span>
                            </div>
                          </div>
                        ))}
                        {dayPosts.length > 3 && (
                          <div className="text-xs text-muted-foreground">
                            +{dayPosts.length - 3} más
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-muted"></div>
                  <span className="text-muted-foreground">Borrador</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-accent/40"></div>
                  <span className="text-muted-foreground">Programado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-primary/40"></div>
                  <span className="text-muted-foreground">Publicado</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Post Detail Dialog */}
          <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Detalle del Post</DialogTitle>
              </DialogHeader>
              {selectedPost && (
                <div className="space-y-4">
                  <p className="text-foreground">{selectedPost.content}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {selectedPost.platforms?.map((platform) => (
                      <Badge key={platform} variant="secondary" className="flex items-center gap-1">
                        {platformIcons[platform]}
                        <span className="capitalize">{platform}</span>
                      </Badge>
                    ))}
                  </div>

                  {selectedPost.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selectedPost.hashtags.map((tag, i) => (
                        <span key={i} className="text-sm text-primary">#{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {selectedPost.scheduled_at && (
                      <span>
                        {format(new Date(selectedPost.scheduled_at), "PPpp", { locale: es })}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge className={statusColors[selectedPost.status]}>
                      {selectedPost.status === 'draft' ? 'Borrador' :
                       selectedPost.status === 'scheduled' ? 'Programado' : 'Publicado'}
                    </Badge>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button 
                      variant="outline"
                      onClick={() => handleDeletePost(selectedPost.post_id)}
                      className="text-destructive hover:text-destructive"
                      data-testid="delete-post-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                    <Link to={`/composer?edit=${selectedPost.post_id}`}>
                      <Button data-testid="edit-post-btn">
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Date Posts Dialog */}
          <Dialog open={!!selectedDate && !selectedPost} onOpenChange={() => setSelectedDate(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Posts del {selectedDate && format(selectedDate, "d 'de' MMMM", { locale: es })}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedDate && getPostsForDate(selectedDate).length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No hay posts para este día</p>
                    <Link to="/composer">
                      <Button>
                        <Plus className="w-4 h-4 mr-2" />
                        Crear Post
                      </Button>
                    </Link>
                  </div>
                ) : (
                  selectedDate && getPostsForDate(selectedDate).map((post) => (
                    <div 
                      key={post.post_id}
                      className="p-4 border border-border rounded-lg cursor-pointer hover:bg-secondary/30 transition-colors"
                      onClick={() => setSelectedPost(post)}
                    >
                      <p className="text-sm line-clamp-2 mb-2">{post.content}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          {post.platforms?.map((platform) => (
                            <span 
                              key={platform}
                              className={`p-1 rounded text-white text-xs ${platformColors[platform]}`}
                            >
                              {platformIcons[platform]}
                            </span>
                          ))}
                        </div>
                        <Badge className={statusColors[post.status]} variant="outline">
                          {post.status === 'draft' ? 'Borrador' :
                           post.status === 'scheduled' ? 'Programado' : 'Publicado'}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};
